// =============================================
// Layer 2+3 — Internal + External Links Audit
// FREE (HTTP HEAD), ~100ms/link, concurrent batch
// =============================================
// Sprawdza:
//   - Każdy <a href="..."> w final_html
//   - HTTP HEAD per URL (timeout 5s) → status 2xx OK, 3xx redirect, 4xx broken, 5xx server
//   - Klasyfikacja: internal (cross-domain match siteUrl) vs external
//   - Anchor text non-empty
// =============================================

export interface LinkCheckResult {
  url: string;
  anchor_text: string;
  status_code: number | null;
  ok: boolean;
  redirect_to?: string;
  error?: string;
  cors_blocked?: boolean;
}

function isCorsLikeError(msg: string): boolean {
  // Browser fetch CORS errors zwykle mają generic "Failed to fetch" / "NetworkError" / "load failed".
  // Brak granularity = wszystkie cross-origin failures wyglądają tak samo. Treat any "fetch failed"
  // jako CORS_BLOCKED — nie wiemy czy real broken czy CORS, więc graceful nie kara user'a.
  const m = msg.toLowerCase();
  return (
    m.includes("failed to fetch") ||
    m.includes("network") ||
    m.includes("load failed") ||
    m.includes("cors") ||
    m.includes("aborted")
  );
}

export interface LinksAuditResult {
  status: "ok" | "warning" | "failed";
  internal: {
    checked: number;
    broken: number;
    issues: string[];
    results: LinkCheckResult[];
  };
  external: {
    checked: number;
    broken: number;
    issues: string[];
    results: LinkCheckResult[];
  };
}

const HEAD_TIMEOUT_MS = 5000;
const MAX_CONCURRENT = 5;

function extractLinks(html: string): Array<{ url: string; anchor: string }> {
  const links: Array<{ url: string; anchor: string }> = [];
  const regex = /<a\b[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(html)) !== null) {
    const url = m[1].trim();
    const anchor = m[2].replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    if (!url || url.startsWith("#") || url.startsWith("mailto:") || url.startsWith("tel:")) continue;
    links.push({ url, anchor });
  }
  return links;
}

function getDomain(url: string, baseUrl?: string): string | null {
  try {
    // Z baseUrl: relative URL ("/foo") jest resolvowany do absolute (siteUrl + "/foo").
    // Bez baseUrl: tylko absolute URL parsowany; relative rzuca i return null.
    const u = baseUrl ? new URL(url, baseUrl) : new URL(url);
    return u.hostname;
  } catch {
    return null;
  }
}

/** Zwraca absolute URL dla href który może być relative ("/foo") lub absolute. */
function resolveUrl(href: string, baseUrl: string): string {
  try {
    return new URL(href, baseUrl).toString();
  } catch {
    return href;
  }
}

async function checkLinkHead(url: string): Promise<LinkCheckResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HEAD_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      method: "HEAD",
      signal: controller.signal,
      redirect: "follow",
    });
    clearTimeout(timer);
    const ok = response.status >= 200 && response.status < 400;
    return {
      url,
      anchor_text: "",
      status_code: response.status,
      ok,
      redirect_to: response.redirected ? response.url : undefined,
    };
  } catch (err) {
    clearTimeout(timer);
    const msg = err instanceof Error ? err.message : "fetch failed";
    const corsBlocked = isCorsLikeError(msg);
    return {
      url,
      anchor_text: "",
      status_code: null,
      ok: corsBlocked, // CORS-blocked → treat as "unknown OK" (graceful), nie penalize
      error: msg,
      cors_blocked: corsBlocked,
    };
  }
}

// Batch z concurrency limit
async function batchCheckLinks(
  links: Array<{ url: string; anchor: string }>,
): Promise<LinkCheckResult[]> {
  const results: LinkCheckResult[] = new Array(links.length);
  let cursor = 0;
  async function worker(): Promise<void> {
    while (cursor < links.length) {
      const idx = cursor++;
      const { url, anchor } = links[idx];
      const result = await checkLinkHead(url);
      result.anchor_text = anchor;
      results[idx] = result;
    }
  }
  const workers = Array.from({ length: Math.min(MAX_CONCURRENT, links.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

export async function auditLinks(
  finalHtml: string,
  siteUrl: string,
): Promise<LinksAuditResult> {
  const siteDomain = getDomain(siteUrl) || "";
  const allLinks = extractLinks(finalHtml).map((l) => ({
    url: resolveUrl(l.url, siteUrl), // relative "/foo" → "https://site.com/foo"
    anchor: l.anchor,
  }));

  const internalLinks = allLinks.filter((l) => {
    const d = getDomain(l.url, siteUrl);
    return d && (d === siteDomain || d.endsWith("." + siteDomain));
  });
  const externalLinks = allLinks.filter((l) => {
    const d = getDomain(l.url, siteUrl);
    return d && d !== siteDomain && !d.endsWith("." + siteDomain);
  });

  // NIE robimy HTTP HEAD na linki — browser blokuje cross-origin (CORS) na own-domain
  // (dashboard.X != site.Y) + external. Spam CORS w konsoli + zero wartości audytowej.
  // Pełne checking wymaga Supabase Edge Function 'audit-fetch' (osobny ticket).
  // Tymczasem: tylko zliczamy linki + check anchor text non-empty.
  const internalResults: LinkCheckResult[] = internalLinks.map((l) => ({
    url: l.url,
    anchor_text: l.anchor,
    status_code: null,
    ok: true,
  }));
  const externalResults: LinkCheckResult[] = externalLinks.map((l) => ({
    url: l.url,
    anchor_text: l.anchor,
    status_code: null,
    ok: true,
  }));

  const internalIssues: string[] = [];
  const externalIssues: string[] = [];

  // Anchor text emptiness — informational (możliwy SEO problem)
  for (const r of internalResults) {
    if (!r.anchor_text) {
      internalIssues.push(`Pusty anchor: ${r.url}`);
    }
  }
  if (externalLinks.length > 0) {
    externalIssues.push(
      `${externalLinks.length} link(ów) zewnętrznych — counted only (HTTP check wymaga server-side proxy)`,
    );
  }
  if (internalLinks.length > 0) {
    internalIssues.push(
      `${internalLinks.length} link(ów) wewnętrznych — counted only (HTTP check wymaga server-side proxy)`,
    );
  }

  // status zawsze ok (nie sprawdzamy HTTP — nie znamy broken)
  const internalBroken: LinkCheckResult[] = [];

  // Status: zawsze ok (HTTP HEAD skipped). Real broken links wymaga server proxy.
  const status: LinksAuditResult["status"] = "ok";
  void internalBroken;

  return {
    status,
    internal: {
      checked: internalResults.length,
      broken: internalBroken.length,
      issues: internalIssues,
      results: internalResults,
    },
    external: {
      checked: externalLinks.length, // counted ale NIE sprawdzane
      broken: 0,
      issues: externalIssues,
      results: externalResults,
    },
  };
}
